import axios from 'axios';
import i18n from '../i18n';

// API Base URL - automatically switches based on environment
// 生产环境使用相对路径（通过 Nginx 代理），开发环境使用 localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:8000');
// Image Base URL - for displaying uploaded images
export const IMAGE_BASE_URL = import.meta.env.VITE_IMAGE_BASE_URL || (import.meta.env.PROD ? '' : 'http://localhost:8000');
// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add authentication token here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - unified error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // If request is cancelled, throw the original error directly
    if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
      return Promise.reject(error);
    }

    // Format error information
    const errorResponse = {
      message: i18n.t('common.api.errors.requestFailed'),
      status: error.response?.status,
      data: error.response?.data,
    };

    // Provide more friendly error messages based on status code
    if (error.code === 'ECONNABORTED') {
      errorResponse.message = i18n.t('common.api.errors.requestTimeout');
    } else if (!error.response) {
      errorResponse.message = i18n.t('common.api.errors.networkError');
    } else {
      switch (error.response.status) {
        case 400:
          errorResponse.message = i18n.t('common.api.errors.badRequest');
          break;
        case 401:
          errorResponse.message = i18n.t('common.api.errors.unauthorized');
          break;
        case 403:
          errorResponse.message = i18n.t('common.api.errors.forbidden');
          break;
        case 404:
          errorResponse.message = i18n.t('common.api.errors.notFound');
          break;
        case 500:
          errorResponse.message = i18n.t('common.api.errors.serverError');
          break;
        case 502:
          errorResponse.message = i18n.t('common.api.errors.gatewayError');
          break;
        case 503:
          errorResponse.message = i18n.t('common.api.errors.serviceUnavailable');
          break;
        default:
          errorResponse.message = error.response.data?.message || i18n.t('common.api.errors.requestFailed');
      }
    }

    console.error('API Error:', errorResponse);
    return Promise.reject(errorResponse);
  }
);

/**
 * Generic request wrapper function with retry mechanism
 * @param {Function} requestFn - Request function to execute
 * @param {number} retries - Number of retry attempts
 * @param {number} delay - Retry delay in milliseconds
 */
const requestWithRetry = async (requestFn, retries = 2, delay = 1000) => {
  try {
    return await requestFn();
  } catch (error) {
    if (retries > 0 && (!error.status || error.status >= 500)) {
      // Only retry for server errors or network errors
      await new Promise(resolve => setTimeout(resolve, delay));
      return requestWithRetry(requestFn, retries - 1, delay * 1.5);
    }
    throw error;
  }
};

/**
 * Get carousel data
 */
export const getCarousel = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/carousel', { signal });
    return response.data;
  });
};

/**
 * Get articles list
 */
export const getArticles = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/articles', { signal });
    return response.data;
  });
};

/**
 * Get featured research
 */
export const getResearch = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/research', { signal });
    return response.data;
  });
};

/**
 * Get gallery
 */
export const getGallery = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/gallery', { signal });
    return response.data;
  });
};

/**
 * Get events list
 */
export const getEvents = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/events', { signal });
    return response.data;
  });
};

// ==================== Business API ====================

/**
 * Get all services list
 */
export const getServices = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/services', { signal });
    return response.data;
  });
};

/**
 * Get single service details by slug
 */
export const getServiceBySlug = async (slug, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get(`/api/services/${slug}`, { signal });
    return response.data;
  });
};

/**
 * Get industry insights articles list
 */
export const getInsights = async (params = {}, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/insights', { params, signal });
    // Backend returns {success, data, message}, interceptor already returns response.data
    // So here response is the {success, data, message} object
    return response.data || response;
  });
};

/**
 * Get single insight article details by slug
 */
export const getInsightBySlug = async (slug, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get(`/api/insights/${slug}`, { signal });
    return response.data;
  });
};

/**
 * Get case studies list
 */
export const getCaseStudies = async (params = {}, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/case-studies', { params, signal });
    return response.data;
  });
};

/**
 * Get single case study details by slug
 */
export const getCaseStudyBySlug = async (slug, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get(`/api/case-studies/${slug}`, { signal });
    return response.data;
  });
};

/**
 * Get team members list
 */
export const getTeam = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/team', { signal });
    return response.data;
  });
};

/**
 * Get single team member details by ID
 */
export const getTeamMemberById = async (id, signal) => {
  return requestWithRetry(async () => {
    const response = await api.get(`/api/team/${id}`, { signal });
    return response.data;
  });
};

/**
 * Get company information
 */
export const getCompanyInfo = async (signal) => {
  return requestWithRetry(async () => {
    const response = await api.get('/api/company-info', { signal });
    return response.data;
  });
};

export default api;
